import { useState } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { TaskTemplateType } from '@econtractor/types';
import { useToast } from 'context/ToastContext';
import ESpinner from 'components/others/Spinner';
import TitleCounter from 'components/others/TitleCounter';
import ListToolBarActions from 'components/toolbar/ListToolBarActions';
import SolarIcon from 'components/icons/SolarIcon';
import { deleteTaskTemplate } from 'api/apiTask';
import TaskCategoryFilter from '../TaskCategory/TaskCategoryFilter';
import TaskCategoryMenu from '../TaskCategory/TaskCategoryMenu';
import { useTaskDirRegistry } from './model/useTaskDirList';
import TaskDirTable from './TaskDirTable';
import OffTaskDir from './OffTaskDir';

const TaskDir = () => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { showToast } = useToast();
    const {
        templates,
        showSpinner,
        filterCategoryId,
        setFilterCategoryId,
        filterTypeId,
        setFilterTypeId,
        reload,
        triggerListReset,
        triggerListRefresh,
    } = useTaskDirRegistry();
    const [editorId, setEditorId] = useState<number | null>(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<TaskTemplateType | null>(null);
    const [deleting, setDeleting] = useState(false);

    const openNew = () => {
        setEditorId(null);
        setEditorOpen(true);
    };

    const openEdit = (template: TaskTemplateType) => {
        setEditorId(template.ID);
        setEditorOpen(true);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        const res = await deleteTaskTemplate(deleteTarget.ID);
        setDeleting(false);
        if (!res?.stat) {
            showToast(t('common:messError'), 'danger');
            return;
        }
        setDeleteTarget(null);
        showToast(t('directory.messDeleted'), 'success');
        reload();
    };

    return (
        <>
            <Row className="task-board-page position-relative">
                <Col className="col-sticky" sm={12}>
                    <div className="w-100 d-sm-flex pt-1 justify-content-between align-items-start">
                        <div>
                            <h4 className="p-0 m-0 mb-0 me-2 text-break">{t('directory.title')}</h4>
                            <TitleCounter />
                        </div>
                        <div className="mt-2 mt-sm-0">
                            <div className="mb-3 page-toolbar-actions">
                                <Button size="sm" variant="success" className="fs-14 text-nowrap" onClick={openNew}>
                                    <SolarIcon name="plus" className="fs-14 p-0" />
                                    <span className="d-none d-md-inline ms-1">{t('directory.btnNew')}</span>
                                </Button>
                                <TaskCategoryMenu />
                                <ListToolBarActions onReset={triggerListReset} onRefresh={triggerListRefresh} />
                                <TaskCategoryFilter
                                    filterCategoryId={filterCategoryId}
                                    setFilterCategoryId={setFilterCategoryId}
                                    filterTypeId={filterTypeId}
                                    setFilterTypeId={setFilterTypeId}
                                />
                            </div>
                        </div>
                    </div>
                </Col>
                <Col sm={12}>
                    {templates == null ? (
                        <ESpinner />
                    ) : (
                        <TaskDirTable
                            templates={templates}
                            showSpinner={showSpinner}
                            onEdit={openEdit}
                            onDelete={setDeleteTarget}
                        />
                    )}
                </Col>
            </Row>

            <OffTaskDir
                show={editorOpen}
                templateId={editorId}
                onHide={() => setEditorOpen(false)}
                onSaved={reload}
            />

            <Modal show={Boolean(deleteTarget)} onHide={() => !deleting && setDeleteTarget(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('common:buttons.delete')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {t('directory.confirmDelete', { name: deleteTarget?.taskName || '' })}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" disabled={deleting} onClick={() => setDeleteTarget(null)}>
                        {t('common:buttons.cancel')}
                    </Button>
                    <Button variant="danger" disabled={deleting} onClick={() => void confirmDelete()}>
                        {deleting ? t('common:buttons.deleting') : t('common:buttons.delete')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default TaskDir;
